import { type FC, useCallback } from 'react'

import { Button } from '@/components/button/button'

import { cn } from '@/lib/utils'

import { PricingFeature } from './pricing-feature'

const corePremiumFeatures = [
  'Case tracking for USCIS, NVC, DOL, and EOIR',
  'Automated email notifications',
  'USCIS visa processing times trends',
  'Visa bulletin trends',
  'Bulk case upload',
  'Client portal for real-time case updates',
]

const enhancedPremiumFeatures = ['Lawfully Pro integration with Email', 'Documents (I-797) upload']

export interface PricingPlanProps {
  priceId: string
  name: 'Basic' | 'Premium' | 'Growth'
  price: number
  nCases: number
  freeTrialAvailable: boolean
  disabled?: boolean
  onSelect: (priceId: string) => void
}

export const PricingPlan: FC<PricingPlanProps> = ({
  priceId,
  name,
  price,
  nCases,
  freeTrialAvailable,
  disabled = false,
  onSelect,
}) => {
  const handleSelect = useCallback(() => {
    onSelect(priceId)
  }, [onSelect, priceId])

  return (
    <div className={cn('flex flex-1 flex-col', 'rounded-2xl', 'bg-white', 'p-[30px]', 'lg:max-w-[352px]')}>
      <div className="mb-[24px] flex">
        <div className="flex h-12 items-center justify-center rounded-[10px] bg-black px-4 text-[20px] font-normal leading-5 text-white">
          {name} Plan
        </div>
      </div>

      <div className="mb-[20px] flex items-baseline gap-[7px]">
        <div className="text-[40px] font-bold leading-[56px]">${price}</div>
        <div className="text-[20px] font-medium leading-[28px] text-muted-foreground">/month</div>
      </div>

      <div className="mb-[70px]">
        <div className="flex flex-col gap-2.5">
          <PricingFeature included title={`${new Intl.NumberFormat('en-US').format(nCases)} total cases`} />
          {(name === 'Basic' || name === 'Premium' || name === 'Growth') && (
            <PricingFeature included title="Core Premium Features" features={corePremiumFeatures} />
          )}
          {(name === 'Premium' || name === 'Growth') && (
            <PricingFeature included title="Enhanced Premium Features" features={enhancedPremiumFeatures} />
          )}
        </div>
      </div>

      <Button
        className={cn(
          'mb-4 mt-auto h-[56px] w-full rounded-xl text-[16px] font-medium',
          name === 'Basic' && ['bg-blue-200', 'hover:bg-blue-200/85', 'text-text'],
          name === 'Growth' && [
            'bg-[linear-gradient(95deg,_hsla(226,100%,60%,1.0)_-14.34%,_hsla(303,100%,44%,1.0)_150.79%)]',
            'hover:bg-[linear-gradient(95deg,_hsla(226,100%,60%,0.6)_-14.34%,_hsla(303,100%,44%,0.6)_150.79%)]',
          ]
        )}
        disabled={disabled}
        onClick={handleSelect}
      >
        {freeTrialAvailable ? 'START 14-DAY FREE TRIAL' : `Upgrade to ${name}`}
      </Button>

      <div className="text-center text-base font-medium text-sky-800">You can cancel anytime.</div>
    </div>
  )
}
